<template>
  <div
    v-if="loading"
    class="p-6"
  >
    <BaseSpinner size="medium-small" />
  </div>
  <div v-else>
    <div
      v-if="survey"
      class="relative p-6 flex flex-row text-white bg-gradient-to-r from-darkPurple to-darkBlue pb-14"
    >
      <div class="flex flex-col">
        <p class="text-2xl">
          {{ survey.name }}
        </p>
        <p class="py-2">
          {{ verdict?.title }}
        </p>
        <p class="pt-5">
          {{ verdict?.description || "" }}
        </p>
      </div>
      <div
        v-if="verdict?.options?.isScoreEssential"
        class="px-5 flex flex-1 flex-col align-end"
      >
        <span class="text-5xl bg-white text-primary px-5 pb-3 pt-4 rounded-full">{{ verdict.score }}</span>
      </div>
      <div class="absolute right-4 bottom-4">
        <DMPButton class="mx-4" />
        <SurveyMSSanteButton
          compact
          :survey-instance-id="instanceId"
          :consumer-key="consumerKey ?? undefined"
        />
        <base-button
          :loading="downloading"
          icon="mdi-download"
          density="comfortable"
          class="rounded-full text-primary mx-4"
          @click="download"
        />
        <base-button
          :loading="pdfLoading"
          icon="mdi-printer"
          class="rounded-full text-primary"
          density="comfortable"
          @click="print"
        />
      </div>
    </div>
    <BaseCard v-if="summary" class="space-y-4">
      <BaseCardHeader>
        <h2>
          Résumé
          <Icon name="mdi-creation" color="primary" />
        </h2>
      </BaseCardHeader>
      <BaseCardBody>
        <MarkdownView :content="summary" />
      </BaseCardBody>
    </BaseCard>
    <div
      v-if="!!survey?.sections"
      class="p-3"
    >
      <SurveyBuiltSection
        v-for="section in survey.sections"
        :key="`section-${section.id}`"
        :section="section"
        :survey-id="survey.id"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {surveyApi} from '@/container';
import BaseSpinner from '@/core/components/base/spinner/BaseSpinner.vue';
import SurveyBuiltSection from '@/survey/components/SurveyBuiltSection.vue';
import {Survey, SurveyVerdict} from "@/core/interfaces/survey/Survey";
import Icon from "@/core/components/icon/Icon.vue";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardHeader from "@/core/components/base/BaseCardHeader.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import DMPButton from "@/core/components/buttons/DMPButton.vue";
import SurveyMSSanteButton from "@/mssante/components/SurveyMSSanteButton.vue";

export default defineComponent({
    name: 'SurveyBuilt',
    components: {
        DMPButton,
        SurveyMSSanteButton,
        BaseButton, BaseCardBody, BaseCardHeader, BaseCard, MarkdownView, Icon, SurveyBuiltSection, BaseSpinner
    },
    props: {
        instanceId: {
            type: Number,
            required: true
        },
    },
    data() {
        return {
            survey: null as Survey | null,
            verdict: null as SurveyVerdict | null,
            summary: null as string | null,
            consumerKey: null as string | null,
            loading: false,
            pdfLoading: false,
            downloading: false
        };
    },
    watch: {
        instanceId: {
            immediate: true,

            handler() {
                if (this.instanceId) {
                    this.loading = true;
                    surveyApi()
                        .builtAnsweredInstance(this.instanceId)
                        .then((response) => {
                            this.survey = response.survey;
                            this.verdict = response.verdict ?? null;
                            this.summary = response.ai_summary?.summary ?? null;
                            this.consumerKey = response.consumer_key ?? null;
                        })
                        .finally(() => {
                            this.loading = false;
                        });
                }
            }
        }
    },
    methods: {
        /**
         * Print a survey built.
         */
        print() {
            if (this.$route.params.instanceId) {
                this.pdfLoading = true;
                surveyApi().export(this.$route.params.instanceId as string)
                    .then(response => {
                        const url = window.URL.createObjectURL(response);
                        window.open(url);
                    })
                    .finally(() => this.pdfLoading = false);
            }
        },
        /**
         * Download a survey built.
         */
        download() {
            if (this.$route.params.instanceId) {
                this.downloading = true;
                surveyApi().export(this.$route.params.instanceId as string)
                    .then(response => {
                        const link = document.createElement("a");
                        link.href = window.URL.createObjectURL(response);
                        link.download = (this.survey as Survey)?.name ? (this.survey as Survey).name + ".pdf" : "questionnaire.pdf";
                        link.click();
                    })
                    .finally(() => this.downloading = false);
            }
        }
    }
});
</script>
